/* Fortschritt des laufenden Compile-Schritts aus dem Log, unabhängig von der Engine.
 * SDHLT schreibt Zeilen wie
 *   "335 /  3044  (11%: est. time to completion 2/2/0 secs)"   oder   "2649 /  3044  (87%: est. time to completion <1 sec)"
 * (der Worker fasst sie zu je einer Zeile je Takt zusammen), die Rust-Ports eine Zeile je Prozent mit Phasenname:
 *   "PortalFlow 12% (eta 30s)"   "BSP models 5% (3/60)"   "GatherLight (bounce) 40%"   "PortalFlow 100% done (1.23s)"
 * Ohne DOM. */

export interface StepProgress {
  /** erledigt/gesamt; null, wenn die Engine nur Prozent meldet (Rust-Ports) */
  done: number | null;
  total: number | null;
  /** 0 bis 100 */
  percent: number;
  /** geschätzte Restzeit in Sekunden (kleinster und größter Wert der Schätzungen), falls angegeben */
  eta: { min: number; max: number } | null;
  /** Phase, wenn die Engine sie nennt (Rust-Ports) */
  label?: string;
}

const LINE = /^\s*(\d+)\s*\/\s*(\d+)(?:\s+\((\d+)%(?::\s*est\. time to completion\s+([^)]*))?\))?\s*$/;

// Rust-Ports: "<Phase> N%" mit optional "(eta Ns)", "(done/total)" oder "done (Zeit)"
const RUST_LINE = /^\s*([A-Za-z][\w ()-]*?)\s+(\d{1,3})%(?:\s+\((?:eta\s+(\d+)s|(\d+)\/(\d+))\)|\s+done\b.*)?\s*$/;

function parseEta(s: string | undefined): StepProgress['eta'] {
  if (!s) return null;
  const nums = (s.match(/<?\d+/g) || []).map((n) => (n[0] === '<' ? 0 : Number(n)));
  if (!nums.length) return null;
  return { min: Math.min(...nums), max: Math.max(...nums) };
}

/** Letzter Fortschritt des aktuellen Schritts (nach der letzten „==== … ====“-Markierung); null, wenn keiner angezeigt wird */
export function parseProgress(log: string): StepProgress | null {
  if (!log) return null;
  const mark = log.lastIndexOf('\n==== ');
  const lines = (mark >= 0 ? log.slice(mark + 1) : log).split(/[\r\n]+/);
  if (mark >= 0 && lines.length) lines.shift();   // die Markierungszeile selbst
  for (let i = lines.length - 1; i >= 0; i--) {
    const r = RUST_LINE.exec(lines[i]);
    if (r && Number(r[2]) <= 100) {
      const done = r[4] !== undefined ? Number(r[4]) : null, total = r[5] !== undefined ? Number(r[5]) : null;
      return { done, total, percent: Number(r[2]), eta: r[3] !== undefined ? { min: Number(r[3]), max: Number(r[3]) } : null, label: r[1] };
    }
    const m = LINE.exec(lines[i]);
    if (!m) continue;
    const done = Number(m[1]), total = Number(m[2]);
    if (!total || done > total) continue;
    return { done, total, percent: m[3] ? Number(m[3]) : Math.floor((done / total) * 100), eta: parseEta(m[4]) };
  }
  return null;
}

/** Restzeit als Text, z. B. "noch 44 min" oder "noch 44–117 min"; unter einer Minute in Sekunden. ‹fmt› formatiert Sekunden. */
export function etaText(eta: StepProgress['eta'], fmt: (sec: number) => string): string {
  if (!eta) return '';
  const lo = fmt(eta.min), hi = fmt(eta.max);
  return lo === hi ? lo : lo + ' – ' + hi;
}
